import { Matrix4, Vector3 } from 'three';
import type { WorldFeature } from '../types';
import { createCitizenBatch } from './citizens/citizenBatch';
import { citizenGeometry } from './citizens/citizenShapes';
import { clipPoseAt, walkPose } from './citizens/citizenPose';
import { CHILDREN_PER_GROUND, childDistanceAt, planActivityGround } from './activityPlan';
import { alongLine, lineOf, type OnLine } from './crowd/walkLine';
import { WALKING } from './crowd/crowdSim';
import { createHeadings } from './heading';
import { groundOf } from './crowd/walkNet';
import { planNeighbourhoodPlay } from './neighbourhoodPlay';

/** Performers stay on the squares; running children stay beside their neighbourhood homes. */
export const activityFeature: WorldFeature = {
  id: 'square-activity',
  label: 'Musicians and children',
  weight: 0.3,
  build(ctx) {
    const asset = ctx.citizens,
      crowd = ctx.lifePlan?.crowd;
    if (!asset || !crowd) return { objects: [], animated: [], obstacles: [], counts: {} };
    const grounds = planActivityGround(ctx.layout, crowd);
    const neighbourhoods = planNeighbourhoodPlay(
      ctx.layout,
      crowd,
      groundOf(ctx.layout, ctx.site, ctx.lots)
    ).slice(0, 4);
    const figures = asset.figures.filter((f) =>
      ['musician', 'child_boy', 'child_girl'].includes(f.trade)
    );
    if (!grounds.length || !figures.length)
      return { objects: [], animated: [], obstacles: [], counts: {} };
    const batch = createCitizenBatch(
      ctx.materials.painted(),
      figures.map((f) => citizenGeometry(asset, f.lods[0]!)),
      grounds.length + neighbourhoods.length * CHILDREN_PER_GROUND,
      'square-musicians-children'
    );
    const music = figures.flatMap((f, i) => (f.trade === 'musician' ? [i] : []));
    const children = figures.flatMap((f, i) => (f.trade.startsWith('child_') ? [i] : []));
    if (!music.length || !children.length)
      throw new Error('square activities: missing citizen figures');
    ctx.materials.setCitizenPoses(asset.poses, asset.bones.length);
    const lines = neighbourhoods.map((g) =>
      lineOf(
        g.route.map(([x, y]) => [x, y, ctx.site.queries.groundHeightAt([x, y])]),
        WALKING.cornerM
      )
    );
    const base = new Matrix4(),
      scale = new Vector3();
    const at: OnLine = { x: 0, y: 0, ground: 0, heading: 0 };
    const headings = createHeadings(neighbourhoods.length * CHILDREN_PER_GROUND);
    let day = true;
    const animated = {
      id: 'square-activities',
      setTimeOfDay(_minutes: number, activity: number) {
        day = activity > 0.05;
      },
      update(seconds: number) {
        headings.begin(seconds);
        batch.begin();
        if (day) {
          grounds.forEach((ground, g) => {
            const musician = music[g % music.length]!;
            const [x, y] = ground.musician;
            base
              .makeRotationY(g * 1.7)
              .scale(scale.setScalar(0.98))
              .setPosition(x, ctx.site.queries.groundHeightAt([x, y]), -y);
            batch.draw(
              musician,
              base,
              clipPoseAt(asset.clips[figures[musician]!.clips.work]!, seconds + g)
            );
          });
          neighbourhoods.forEach((_neighbourhood, g) => {
            for (let k = 0; k < CHILDREN_PER_GROUND; k++) {
              const child = children[(g * CHILDREN_PER_GROUND + k) % children.length]!;
              const size = 0.61 + (k % 3) * 0.055;
              const line = lines[g]!;
              const distance = childDistanceAt(seconds, g, k, line.length);
              alongLine(
                line,
                (((distance % line.length) + line.length) % line.length) / line.length,
                at
              );
              base
                .makeRotationY(headings.at(g * CHILDREN_PER_GROUND + k, at.heading))
                .scale(scale.setScalar(size))
                .setPosition(at.x, at.ground, -at.y);
              const clip =
                figures[child]!.sex === 'f' ? 'VisitorRunForwardSkirt' : 'VisitorRunForward';
              batch.draw(child, base, walkPose(asset.clips[clip]!, distance, size));
            }
          });
        }
        batch.end();
      },
    };
    animated.update(0);
    return {
      objects: [batch.mesh],
      animated: [animated],
      obstacles: [],
      counts: { musicians: grounds.length, children: neighbourhoods.length * CHILDREN_PER_GROUND },
    };
  },
};
